/**
 * Shared Docling document-upload support contract for /try and SSR surfaces.
 *
 * This module is the canonical source of truth for:
 * - Accepted extensions/MIME rows for local uploads.
 * - Shared upload byte cap and `accept` attribute string.
 * - Cross-surface helper behavior for normalizing content types.
 * - Stable file title derivation for non-text submit paths.
 *
 * The values mirror the Docling matrix described in:
 * - /Users/mm/Documents/GitHub/docling/README.md
 * - /Users/mm/Documents/GitHub/docling/packages/docling-api/app/format_matrix.py
 */

import { extractFileExtension } from './file-extension.js';

const MAX_DOCUMENT_BYTES = 30 * 1024 * 1024;

const DOCX_EXTENSIONS = ['.docx', '.dotx', '.docm', '.dotm'] as const;
const XLSX_EXTENSIONS = ['.xlsx', '.xlsm'] as const;
const PPTX_EXTENSIONS = ['.pptx', '.potx', '.ppsx', '.pptm', '.potm', '.ppsm'] as const;
const HTML_EXTENSIONS = ['.html', '.htm', '.xhtml', '.xml', '.nxml', '.xbrl'] as const;
const TEXT_EXTENSIONS = ['.txt', '.text'] as const;
const MARKDOWN_EXTENSIONS = ['.md', '.markdown', '.qmd', '.rmd'] as const;
const ASCIIDOC_EXTENSIONS = ['.adoc', '.asciidoc', '.asc'] as const;
const LATEX_EXTENSIONS = ['.tex', '.latex'] as const;

const DOCX_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template'
] as const;
const XLSX_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
] as const;
const PPTX_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.presentationml.template',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.presentationml.slideshow'
] as const;

const ACCEPT_MIME_TYPES = [
  'application/pdf',
  ...DOCX_MIME_TYPES,
  ...XLSX_MIME_TYPES,
  ...PPTX_MIME_TYPES,
  'text/html',
  'application/xhtml+xml',
  'text/plain',
  'application/csv',
  'text/csv',
  'text/markdown',
  'text/x-markdown',
  'text/asciidoc',
  'application/xml',
  'text/x-tex',
  'application/x-tex',
  'text/x-latex'
] as const;

const ALL_ACCEPT_EXTENSIONS = [
  '.pdf',
  ...DOCX_EXTENSIONS,
  ...XLSX_EXTENSIONS,
  ...PPTX_EXTENSIONS,
  '.csv',
  ...MARKDOWN_EXTENSIONS,
  ...HTML_EXTENSIONS,
  ...TEXT_EXTENSIONS,
  ...ASCIIDOC_EXTENSIONS,
  ...LATEX_EXTENSIONS
] as const;

const TEXT_FILE_EXTENSIONS_SET = new Set<string>(TEXT_EXTENSIONS);
const MARKDOWN_EXTENSIONS_SET = new Set<string>(MARKDOWN_EXTENSIONS);
const LATEX_EXTENSIONS_SET = new Set<string>(LATEX_EXTENSIONS);
const ASCIIDOC_EXTENSIONS_SET = new Set<string>(ASCIIDOC_EXTENSIONS);

const EXTENSION_TO_MIME = new Map<string, string>([
  ...DOCX_EXTENSIONS.map((ext) => [ext, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'] as const),
  ...XLSX_EXTENSIONS.map((ext) => [ext, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] as const),
  ...PPTX_EXTENSIONS.map((ext) => [ext, 'application/vnd.openxmlformats-officedocument.presentationml.presentation'] as const),
  ...HTML_EXTENSIONS.map((ext) => [
    ext,
    ext === '.xhtml'
      ? 'application/xhtml+xml'
      : (ext === '.html' || ext === '.htm' ? 'text/html' : 'application/xml')
  ] as const),
  ...TEXT_EXTENSIONS.map((ext) => [ext, 'text/plain'] as const),
  ...MARKDOWN_EXTENSIONS.map((ext) => [ext, 'text/markdown'] as const),
  ...ASCIIDOC_EXTENSIONS.map((ext) => [ext, 'text/asciidoc'] as const),
  ...LATEX_EXTENSIONS.map((ext) => [ext, 'text/x-tex'] as const)
]);

const MIME_CANONICAL_MAP = new Map<string, string>([
  ['application/pdf', 'application/pdf'],
  ['text/plain', 'text/plain'],
  ['text/markdown', 'text/markdown'],
  ['text/x-markdown', 'text/x-markdown'],
  ['text/asciidoc', 'text/asciidoc'],
  ['text/html', 'text/html'],
  ['application/xhtml+xml', 'application/xhtml+xml'],
  ['text/csv', 'text/csv'],
  ['application/csv', 'application/csv'],
  ['text/x-tex', 'text/x-tex'],
  ['application/x-tex', 'application/x-tex'],
  ['text/x-latex', 'text/x-latex'],
  ['application/xml', 'application/xml'],
  ...DOCX_MIME_TYPES.map((mime) => [mime, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'] as const),
  ...XLSX_MIME_TYPES.map((mime) => [mime, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] as const),
  ...PPTX_MIME_TYPES.map((mime) => [mime, 'application/vnd.openxmlformats-officedocument.presentationml.presentation'] as const)
]);

const ACCEPT_ATTRIBUTE_TOKENS = Array.from(
  new Set([
    ...ALL_ACCEPT_EXTENSIONS,
    ...ACCEPT_MIME_TYPES
  ])
).sort();

/**
 * Exported contract for docling-backed web uploads.
 */
export const TryDocumentFileSupport = Object.freeze({
  MAX_FILE_BYTES: MAX_DOCUMENT_BYTES,
  ACCEPT_ATTR: ACCEPT_ATTRIBUTE_TOKENS.join(','),
  EXACT_TYPE_HINT: 'PDF, DOCX, XLSX, PPTX, HTML, CSV, TXT, Markdown, AsciiDoc, LaTeX, XML',
  GROUPED_PILLS: ['PDF', 'Office', 'Web', 'Text', 'Audio', 'Video']
} as const);

/**
 * Returns true when a local file should be sent through the Docling document uploader.
 */
export function isDoclingDocumentFile(file: File): boolean {
  return normalizeDocumentUploadContentType(file.type, file.name) !== null;
}

/**
 * Normalizes a client-reported content type so routing decisions are consistent.
 *
 * Rules:
 * - Prefer extension-based MIME for known document formats.
 * - Remap markdown/plaintext edge cases to Docling-specific MIME forms.
 * - Canonicalize common aliases to one representative value.
 */
export function normalizeDocumentUploadContentType(value: string, fileName: string): string | null {
  const extension = extractFileExtension(fileName);
  const normalizedValue = value?.trim().toLowerCase().split(';')[0].trim() ?? '';

  if (extension) {
    const extensionKey = `.${extension}`;

    if (TEXT_FILE_EXTENSIONS_SET.has(extensionKey)) {
      if (normalizedValue === 'text/markdown' || normalizedValue === 'text/x-markdown' || normalizedValue === 'application/markdown') {
        return 'text/markdown';
      }
      return 'text/plain';
    }

    if (MARKDOWN_EXTENSIONS_SET.has(extensionKey)) {
      return 'text/markdown';
    }

    if (ASCIIDOC_EXTENSIONS_SET.has(extensionKey)) {
      return 'text/asciidoc';
    }

    if (LATEX_EXTENSIONS_SET.has(extensionKey)) {
      return 'text/x-tex';
    }

    if (extensionKey === '.xml' || extensionKey === '.nxml' || extensionKey === '.xbrl') {
      return 'application/xml';
    }

    const extensionBased = EXTENSION_TO_MIME.get(extensionKey);
    if (extensionBased) {
      return extensionBased;
    }
  }

  if (!normalizedValue) {
    return null;
  }

  return MIME_CANONICAL_MAP.get(normalizedValue) ?? null;
}

/**
 * Creates a short upload title from a filename.
 *
 * This keeps shared submit labels stable across SSR and client surfaces.
 */
export function deriveDocumentTitle(fileName: string): string {
  const trimmedName = fileName?.trim();
  if (!trimmedName) {
    return 'Uploaded document';
  }

  const baseName = trimmedName.replace(/.*[\\/]/, '');
  const withoutExtension = baseName.replace(/\.[^.]+$/, '').trim();
  return withoutExtension || baseName;
}

